import React from 'react';
import { Button, Input, Modal, Popconfirm, Progress, Select, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { formatBytes, mapLayerPhaseText, mapLayerRightStatus } from '../../utils/orthoToolbox';

/**
 * 路网 / 等高线管理弹窗
 */
export default function OrthoMapLayerManageModal({
  open,
  mapManageKind,
  mapLayerTitle,
  mapLayerDescription,
  contourInterval,
  mapLayerBusy,
  mapLayerUpload,
  mapLayerTasks,
  mapLayerFileRef,
  onClose,
  onManageKindChange,
  onTitleChange,
  onDescriptionChange,
  onContourIntervalChange,
  onCreateTask,
  onClearUpload,
  onFileInputChange,
  onPickFile,
  onStartSlice,
  onRequeue,
  onCancelTask,
  onDeleteTask
}) {
  return (
    <Modal
      open={open}
      title={`管理${mapManageKind === 'contour' ? '等高线' : '路网'}图层`}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose={false}
      styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
    >
      <div className="zhxt-ortho-muted" style={{ marginBottom: 8 }}>
        新建 → 上传源文件 → 启动切片。完成后在左侧勾选叠加。
        成品删除与 OSS 清理请到管理后台「叠加图层」。
      </div>
      <Space.Compact style={{ width: '100%', marginTop: 6 }}>
        <Select
          size="small"
          style={{ width: 96 }}
          value={mapManageKind}
          onChange={onManageKindChange}
          options={[
            { value: 'road', label: '路网' },
            { value: 'contour', label: '等高线' }
          ]}
        />
        <Input
          size="small"
          placeholder="任务名称（竖型菜单显示）"
          value={mapLayerTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          maxLength={200}
        />
        <Button size="small" type="primary" loading={mapLayerBusy} onClick={onCreateTask}>
          新建
        </Button>
      </Space.Compact>
      <Input.TextArea
        size="small"
        style={{ marginTop: 6 }}
        rows={2}
        placeholder="描述（鼠标悬停菜单名称时显示，可选）"
        value={mapLayerDescription}
        onChange={(e) => onDescriptionChange?.(e.target.value)}
        maxLength={500}
        showCount
      />
      {mapManageKind === 'contour' && (
        <div style={{ marginTop: 6 }}>
          <span className="zhxt-ortho-muted">等高距 </span>
          <Select
            size="small"
            style={{ width: 88 }}
            value={contourInterval}
            onChange={onContourIntervalChange}
            options={[
              { value: 5, label: '5 m' },
              { value: 10, label: '10 m' },
              { value: 20, label: '20 m' }
            ]}
          />
        </div>
      )}
      {mapLayerUpload && (
        <div
          className={`zhxt-map-layer-progress ${mapLayerUpload.phase === 'error' ? 'is-error' : ''} ${mapLayerUpload.done ? 'is-done' : ''}`}
        >
          <div className="zhxt-map-layer-progress-title">
            任务 #{mapLayerUpload.taskId}
            {mapLayerUpload.fileName ? ` · ${mapLayerUpload.fileName}` : ''}
          </div>
          <div className="zhxt-map-layer-progress-text">{mapLayerPhaseText(mapLayerUpload)}</div>
          {(mapLayerUpload.phase === 'picking' || mapLayerUpload.phase === 'credentials'
            || mapLayerUpload.phase === 'uploading' || mapLayerUpload.phase === 'registering'
            || mapLayerUpload.done) && (
            <Progress
              percent={
                mapLayerUpload.phase === 'picking' || mapLayerUpload.phase === 'credentials'
                  ? 0
                  : (mapLayerUpload.percent || 0)
              }
              size="small"
              status={mapLayerUpload.done ? 'success' : 'active'}
              showInfo={mapLayerUpload.phase === 'uploading' || mapLayerUpload.done || mapLayerUpload.phase === 'registering'}
            />
          )}
          {mapLayerUpload.phase === 'error' && (
            <Button
              size="small"
              type="link"
              style={{ padding: 0, height: 'auto', marginTop: 4 }}
              onClick={onClearUpload}
            >
              关闭提示后可重新上传
            </Button>
          )}
        </div>
      )}
      <input
        ref={mapLayerFileRef}
        type="file"
        accept={mapManageKind === 'road' ? '.gpkg,.geojson,.json,.zip,.GPKG' : '.tif,.tiff,.geotiff,.TIF,.TIFF'}
        style={{ display: 'none' }}
        onChange={onFileInputChange}
      />
      <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
        {(mapLayerTasks || []).filter((t) => t.kind === mapManageKind).slice(0, 12).map((t) => {
          const uploadingThis = Number(mapLayerUpload?.taskId) === Number(t.id)
            && !mapLayerUpload.done
            && mapLayerUpload.phase !== 'error';
          const uploadText = uploadingThis ? mapLayerPhaseText(mapLayerUpload) : '';
          const rightStatus = mapLayerRightStatus(t, uploadText);
          const statusClass = uploadingThis || t.status === 'processing' || t.status === 'queued'
            ? 'is-busy'
            : t.status === 'failed'
              ? 'is-failed'
              : t.status === 'succeeded'
                ? 'is-ok'
                : '';
          return (
            <div
              key={t.id}
              className={`zhxt-ortho-task zhxt-map-task ${uploadingThis ? 'active' : ''}`}
              style={{ marginTop: 4 }}
            >
              <div className="zhxt-map-task-row">
                <div className="zhxt-map-task-main">
                  <div className="zhxt-ortho-task-title">
                    #{t.id} {t.kind === 'contour' ? '等高线' : '路网'} {t.title || ''}
                  </div>
                  {t.sourceBytes != null && !uploadingThis && (
                    <div className="zhxt-ortho-muted">{formatBytes(t.sourceBytes)}</div>
                  )}
                  {t.status === 'failed' && t.errorMessage && (
                    <div className="zhxt-ortho-muted" style={{ color: '#b91c1c' }}>{t.errorMessage}</div>
                  )}
                </div>
                <div className={`zhxt-map-task-status ${statusClass}`} title={rightStatus}>
                  {rightStatus}
                </div>
                <div className="zhxt-map-task-actions">
                  <Space size={0} wrap>
                    {['draft', 'failed'].includes(t.status) && (
                      <Button
                        type="link"
                        size="small"
                        loading={uploadingThis}
                        disabled={mapLayerBusy && !uploadingThis}
                        onClick={() => onPickFile(t.id)}
                      >
                        上传
                      </Button>
                    )}
                    {t.status === 'queued' && (
                      <Button
                        type="link"
                        size="small"
                        disabled={mapLayerBusy}
                        onClick={() => onStartSlice(t.id)}
                      >
                        启动切片
                      </Button>
                    )}
                    {['failed', 'succeeded'].includes(t.status) && t.sourceOssKey && (
                      <Button
                        type="link"
                        size="small"
                        disabled={mapLayerBusy}
                        onClick={() => onRequeue(t.id)}
                      >
                        {t.status === 'succeeded' ? '重新切片' : '重新排队'}
                      </Button>
                    )}
                    {['draft', 'queued', 'failed'].includes(t.status) && (
                      <Popconfirm
                        title={`取消任务 #${t.id}？`}
                        description="将放弃任务并清理该任务在 OSS inbox 中的源文件（排队中的也会清除）"
                        okText="取消任务并清理"
                        cancelText="返回"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onCancelTask(t.id)}
                      >
                        <Button type="link" size="small" disabled={mapLayerBusy}>
                          取消
                        </Button>
                      </Popconfirm>
                    )}
                    {!['processing', 'queued'].includes(t.status) && (
                      <Popconfirm
                        title={`删除任务 #${t.id}？`}
                        description="将删除记录并清理该任务 OSS inbox 残留"
                        okText="删除"
                        cancelText="返回"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDeleteTask(t.id)}
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={mapLayerBusy} />
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              </div>
            </div>
          );
        })}
        {(mapLayerTasks || []).filter((t) => t.kind === mapManageKind).length === 0 && (
          <div className="zhxt-ortho-muted">暂无路网/等高线任务，请先「新建」</div>
        )}
      </div>
    </Modal>
  );
}
